import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import * as moment from 'moment';
import { ApiResourceService } from 'libraries/service/apiResource/apiResource.service';
import { IItemData, ILinkSetResponse, IItem, IIdentifiedItem, IItemIdentifier } from 'libraries/interfaces/ISearchResponse';
import { IBookingStatus } from 'libraries/interfaces/watabi/i-booking-status';
import { IItemPostRequest, EItemAction, IItemMoveRequest } from 'libraries/interfaces/watabi/ISearchParameters';
import { flatMap } from "rxjs/internal/operators";
import { CategoryCode } from "libraries/enums/e-category-code";
import { map } from "rxjs/operators";
import {IBlock} from "../../../interfaces/IItemService";

declare const companyId: string;
declare const language: string;
declare const userId: string;
declare const config: { contentLanguage: string; };

const WATABI: string = 'watabi';
const ITEM: string = 'item';
const MEDIA: string = 'media';
const BOOKINGS: string = 'bookings';
const TOTAL: string = 'total';

export enum ItemClass {
    BOOKING = "Booking",
    PRODUCT = "Product",
    SEQUENCE = "Sequence",
    SECTION = "Section",
    DESCRIPTION = "Description",
    BLOCK = "Block",
    MEDIA = "Media",
    QUOTE = "Quote",
    CRITERIA = "Criteria",
    DESTINATION = "Destination",
}

export enum EGetItemDepth {
    Full = 'F'
}

export interface IGetItemOptions {
    cache?: boolean;
    depth?: EGetItemDepth;
}

export interface IWatabiBookingTotal {
    decimals: number;
    currency: string;
    total:  number;
    value:  number;
    amount:  number;
}

@Injectable()
export class WatabiResourceService {

    private idPlaceholder: number = -1;

    constructor(
        private readonly apiResource: ApiResourceService,
    ) {
    }

    public getBookingTotalCost(id: number, category?: CategoryCode): Observable<IWatabiBookingTotal> {
        const url: string = Boolean(category) ?
            this.apiResource.getApiUrl(WATABI, BOOKINGS, TOTAL, id, category.toLowerCase()):
            this.apiResource.getApiUrl(WATABI, BOOKINGS, TOTAL, id);
        return this.apiResource.getOne(
            url,
        ).pipe(
            map((response: IWatabiBookingTotal): IWatabiBookingTotal => {
                if(response.total){
                    return  {
                        ...response,
                        value: response.total,
                        amount: response.total / 100,
                    };
                }
                return response;
            })
        );
    }

    public createQuotes(block: IBlock, beginDate: string): Observable<any> {
        const url: string = this.apiResource.getApiUrl(WATABI, 'quotes', 'create');
        return this.apiResource.postOne(url, this.getCreateQuotesBody(block, beginDate));
    }

    private getCreateQuotesBody(block: IBlock, beginDate: string): any {
        return {
            segment:{ begin: { date: beginDate}},
            externalReferenceArray:[
                {host: 'WATABI', code: block.idRoot.toString(), detail: { class: 'Booking' }},
                {host: 'WATABI', code: block.idItem.toString(), detail:{ class: 'Block' }},
                {host: 'OSMAN', code: block.contracts[0].toString(), detail: { class: 'Contract' }}
            ]
        };
    }

    public deleteMedia(filesList: string[], data: IItem): Observable<IItemData>{
        const url: string = this.apiResource.getApiServerUrl(WATABI, MEDIA);
        return this.apiResource.deleteWithBody(url, filesList)
            .pipe(
                flatMap((): Observable<IItemData> => {
                    return this.deleteItem(data);
                })
            );
    }

    public update<T extends IItemData>(item: T, newProperties: Partial<T>): Observable<IItemData> {
        const request: IItemPostRequest = this.getUpdateRequest(item, newProperties);
        return this.setItem(request);
    }

    public replicate(parent: IItem, replicatedItem: IItem, childPosition?: number): Observable<IItemData> {
        const request: IItemPostRequest = this.getReplicateRequest(
            parent,
            replicatedItem,
            childPosition,
        );
        return this.setItem(request);
    }

    public createChildItem(parent: IItem, childProperties: any, childPosition?: number): Observable<IItem[]> {
        const request: IItemPostRequest[] = this.getChildCreateRequest(parent, childProperties, childPosition);
        return this.setMultipleItems(request);
    }

    public setItem(body): Observable<IItemData> {
        const url: string = this.apiResource.getApiServerUrl(WATABI, ITEM);
        return this.apiResource.postOne(url, prepareItemForUpdate(body));
    }

    public deleteItem(body: IIdentifiedItem): Observable<IItemData> {
        const url: string = this.apiResource.getApiServerUrl(WATABI, ITEM);
        return this.apiResource.postOne(url, this.getDeleteRequest(body));
    }

    public setCriteriaItem(body): Observable<ILinkSetResponse> {
        const url: string = this.apiResource.getApiServerUrl(WATABI, ITEM);
        return this.apiResource.postOne(url, prepareItemForUpdate(body));
    }

    public setMultipleItems(body: IItemPostRequest[]): Observable<IItem[]> {
        const filteredBody: IItemPostRequest[] = body.map(prepareItemForUpdate);
        const url: string = this.apiResource.getApiServerUrl(WATABI, ITEM);
        return this.apiResource.post(url, filteredBody);
    }

    public getItem(idItemOrVersion: string | number, options?: IGetItemOptions): Observable<IItem> {
        const url: string = this.apiResource.getApiServerUrl(WATABI, ITEM, idItemOrVersion);
        return this.apiResource.getOne(url, options);
    }

    public getBookingStatus(idItem: string | number): Observable<IBookingStatus> {
        const url: string = this.apiResource.getApiServerUrl(WATABI, 'bookings', idItem, 'statuses');
        return this.apiResource.getOne(url);
    }

    public compute(idVersionQuotes: string | number): Observable<void> {
        const url: string = this.apiResource.getApiUrl(WATABI, 'quotes', idVersionQuotes, 'compute');
        return this.apiResource.postOne(url, null);
    }

    public computeChildrenItemCreation(parent: IItem, childProperties: any, childPosition?: number): Observable<IItemPostRequest[]> {
        return of(this.getChildCreateRequest(parent, childProperties, childPosition));
    }

    public move(moved: IItemIdentifier, newParent: IItemIdentifier, newParentsChildrenOrder: { [key: string]: number[] }): Observable<IItemData> {
        const request: IItemMoveRequest = this.getMoveRequest(moved, newParent, newParentsChildrenOrder);

        return this.apiResource.postOne(
            this.apiResource.getApiServerUrl(WATABI, ITEM),
            request
        );
    }

    public getMoveRequest(moved: IItemIdentifier, newParent: IItemIdentifier, newParentsChildrenOrder: { [key: string]: number[] }): IItemMoveRequest {
        return {
            action: EItemAction.Move,
            actionDetails: {
                newChild: moved
            },
            idVersion: newParent.idVersion,
            childrenOrder: newParentsChildrenOrder
        };
    }

    public getUpdateRequest<T extends IItemData>(item: T, newProperties: Partial<T>): IItemPostRequest {
        const request: IItemPostRequest = {
            action: EItemAction.Update,
            ...item,
            ...newProperties,
        };
        return prepareItemForUpdate(request);
    }

    public getReplicateRequest(parent: IItem, replicatedItem: IItem, childPosition?: number): IItemPostRequest {
        const idItem: number = this.generateIdPlaceholder();

        const replicateRequest: IItemPostRequest = {
            action: EItemAction.Replicate,
            idItem,
            class: replicatedItem.class,
            idVersion: replicatedItem.idVersion,
            parent: {
                childrenOrder: addItemInChildrenOrder(parent, replicatedItem.class, idItem, childPosition),
                idVersion: parent.idVersion
            },
            ...getInstanceInformations()
        };

        return replicateRequest;
    }

    public getChildCreateRequest(parent: IItem, childProperties: any, childPosition?: number | "0"): IItemPostRequest[] {
        const childCreate: IItemPostRequest = this.getCreateRequest({
            ...childProperties,
            idVersionParent: parent.idVersion
        });
        const parentChildrenOrderUpdate: IItemPostRequest = prepareItemForChildrenOrderUpdate(parent, childProperties.class, childCreate.idItem, childPosition);
        return [
            childCreate,
            parentChildrenOrderUpdate
        ];
    }

    public getDeleteRequest(params: IIdentifiedItem): IItemPostRequest {
        const { idItem, idVersion }: IIdentifiedItem = params;
        return {
            action: EItemAction.Delete,
            idItem,
            idVersion,
        };
    }

    public getCreateRequest(params: any): IItemPostRequest {
        return {
            action: EItemAction.Create,
            idItem: this.generateIdPlaceholder(),
            idVersion: this.generateIdPlaceholder(),
            ...params,
            ...getInstanceInformations()
        };
    }

    private generateIdPlaceholder(): number {
        this.idPlaceholder--;
        return this.idPlaceholder;
    }
}


function prepareItemForUpdate(params: IItemPostRequest): IItemPostRequest {
    if (params.action !== 'U') {
        return params;
    }

    const {
        descriptions, medias, ui, days,
        quotes, sequences, blocks, sections,
        flags, idVersionOrigin, idOrigin, destinations,
        whoLastUpdated, whenLastUpdated, shared,
        idRoot, idVersionRoot, links, index, ...filteredBody
    }: any = params;

    return serializeDates(filteredBody);
}


function addItemInChildrenOrder(params: IItem, childClass: ItemClass, childId: number, childPosition: number | "0"): { [key: string]: number[] } {
    const childrenOrderPropertyKey: string = `${childClass.toLowerCase()}s`;
    if (!params.childrenOrder) {
        params.childrenOrder = {};
    }
    if (!params.childrenOrder[childrenOrderPropertyKey]) {
        params.childrenOrder = {
            ...params.childrenOrder,
            [childrenOrderPropertyKey]: []
        };
    }
    const hasChildrenOrderSet: boolean = Boolean(params.childrenOrder[childrenOrderPropertyKey] && params.childrenOrder[childrenOrderPropertyKey].length);
    if (!hasChildrenOrderSet) {
        return {
            ...params.childrenOrder,
            [childrenOrderPropertyKey]: [childId]
        };
    }

    const hasChildPositionParam: boolean = Boolean(childPosition || childPosition === "0");
    const isChildPositionAtEndPosition: boolean = (hasChildrenOrderSet && !hasChildPositionParam) ||
        (hasChildrenOrderSet && hasChildPositionParam && params.childrenOrder[childrenOrderPropertyKey].length === +childPosition);

    if (isChildPositionAtEndPosition) {
        return {
            ...params.childrenOrder,
            [childrenOrderPropertyKey]: [
                ...params.childrenOrder[childrenOrderPropertyKey],
                childId
            ]
        };
    }
    return {
        ...params.childrenOrder,
        [childrenOrderPropertyKey]: params.childrenOrder[childrenOrderPropertyKey]
            .reduce((acc: number[], property: number, index: number) => {
                const newProps: number[] = +childPosition === index ?
                    [childId, property] : [property];
                return [
                    ...acc,
                    ...newProps
                ];
            }, [])
    };
}

function prepareItemForChildrenOrderUpdate(params: IItem, childClass: ItemClass, childId: number, childPosition: number | "0"): IItemPostRequest {

    return {
        action: EItemAction.Update,
        idItem: params.idItem,
        idVersion: params.idVersion,
        class: params.class,
        childrenOrder: addItemInChildrenOrder(params, childClass, childId, childPosition)
    };
}

function getInstanceInformations(): any {
    return {
        language,
        languagesUsed: config.contentLanguage.toUpperCase(),
        companyOwner: { id: companyId },
        personOwner: { id: userId },
    };
}

/**
 * Prevents sending modified dates to the API
 * (dates are serialized with UTC + offset)
 */
function serializeDates(value: IItemPostRequest): IItemPostRequest {
    return ['begin', 'end']
        .reduce((agg: IItemPostRequest, propertyName: string) => {
            if (agg[propertyName] && agg[propertyName] instanceof Date) {
                return {
                    ...agg,
                    [propertyName]: moment(agg[propertyName]).format('YYYY-MM-DD'),
                };
            }

            return agg;
        }, value);

}
